import { CouponArt } from "@/components/marketing/coupon/CouponArt"
import { CouponTicket } from "@/components/marketing/coupon/CouponTicket"
import { CouponFreeItemAction } from "@/components/marketing/coupon/CouponFreeItemAction"
import { CouponTicketTitle } from "@/components/marketing/coupon/CouponTicketTitle"
import { Button, IconButton, ProgressBar, Text } from "@/components/ui"
import { Close } from "@/icons"
import type { BagCoupon } from "@/lib/bag-coupon"
import { centsToReais, formatBRL } from "@/lib/format-brl"
import { SPRING, TRANSITION } from "@/lib/motion"
import { motion, useReducedMotion } from "motion/react"
import { useState, type ReactNode } from "react"
import { BagCouponStatusLine } from "./BagCouponStatusLine"

type BagCouponCardProps = {
  coupon: BagCoupon
  onRemove: () => void
  /** The field "Trocar" opens, given how to close it; no "Trocar" without it. */
  replaceForm?: (close: () => void) => ReactNode
  /** Where "Adicionar itens" leads while the coupon still waits on the bag; no link without it. */
  onAddItems?: () => void
}

function isSuccess(coupon: BagCoupon): boolean {
  const { status } = coupon
  return status.kind === "applied" || (status.kind === "at-checkout" && status.reason === "delivery")
}

function isWarning(coupon: BagCoupon): boolean {
  const kind = coupon.status.kind
  return kind === "blocked" || kind === "not-usable" || kind === "dropped"
}

function showsMinimum(coupon: BagCoupon): boolean {
  const kind = coupon.status.kind
  return coupon.minimum !== null && (kind === "minimum" || kind === "applied" || kind === "at-checkout" || kind === "checking")
}

function StubArt({ coupon }: { coupon: BagCoupon }) {
  const reduceMotion = useReducedMotion()
  const success = isSuccess(coupon)
  const warning = isWarning(coupon)
  return (
    <motion.div
      key={success ? "success" : warning ? "warning" : "pending"}
      initial={reduceMotion ? { opacity: 0 } : { opacity: 0, scale: 0.7, rotate: -8 }}
      animate={{ opacity: 1, scale: 1, rotate: 0 }}
      transition={reduceMotion ? TRANSITION.fade : SPRING.pop}
    >
      <CouponArt size={42} muted={warning} />
    </motion.div>
  )
}

/**
 * One coupon on the bag as an ivory ticket, three compact rows over the full
 * width: the benefit and code beside ×; where it stands ("Faltam R$ 27,00"
 * with a short bar, or "−R$ 5,30 de desconto") with its validity, all on one
 * line; and the one action ("Adicionar itens", else "Trocar") in the bottom
 * right corner.
 */
export function BagCouponCard({ coupon, onRemove, replaceForm, onAddItems }: BagCouponCardProps) {
  const [replacing, setReplacing] = useState(false)
  const success = isSuccess(coupon)
  const waitsOnBag = coupon.status.kind === "minimum" || coupon.status.kind === "blocked"
  const oneLine = coupon.status.kind === "minimum" || coupon.status.kind === "applied" || coupon.status.kind === "checking"
  const headline = coupon.benefitHeadline?.headline ?? coupon.benefitLabel ?? "Cupom"
  const addItems = onAddItems && waitsOnBag ? onAddItems : null
  const canReplace = !addItems && replaceForm && !replacing
  const capped = coupon.status.kind === "applied" && coupon.status.capped
  const offer = coupon.terms?.offer
  const freeItems = offer?.kind === "free-items"
  const itemChoice = offer?.kind === "free-items" && offer.refs.length > 1 ? (coupon.benefitHeadline?.detail ?? null) : null
  const trailing = [capped ? "máximo do cupom" : null, coupon.validity].filter(Boolean).join(" · ")

  return (
    <CouponTicket as="li" busy={coupon.updating} stub={<StubArt coupon={coupon} />}>
      <div className="min-w-0 py-2.5 pl-3.5 pr-2.5">
        <div className="flex min-w-0 items-center gap-1">
          <div className="min-w-0 flex-1">
            <CouponTicketTitle headline={headline} code={coupon.code} />
          </div>
          <IconButton icon={Close} label={`Remover cupom ${coupon.code}`} onClick={onRemove} size="icon-md" iconSize="sm" className="-my-2.5 -mr-2.5 shrink-0" />
        </div>

        <div className={oneLine ? "mt-1 flex min-w-0 items-center gap-1.5 whitespace-nowrap" : "mt-1 flex min-w-0 flex-wrap items-center gap-x-1.5"}>
          <div className="shrink-0">
            <BagCouponStatusLine coupon={coupon} />
          </div>
          {showsMinimum(coupon) && coupon.minimum ? (
            <ProgressBar
              size="xs"
              value={coupon.minimum.progress}
              tone={success ? "success" : "primary"}
              label={`Pedido mínimo do cupom: ${formatBRL(centsToReais(coupon.minimum.cents))}`}
              valueText={`${Math.round(coupon.minimum.progress * 100)}% do pedido mínimo`}
              className="w-16 shrink-0"
            />
          ) : null}
          {trailing ? (
            <Text as="span" size="xs" tone="subtle" truncate className="min-w-0">
              · {trailing}
            </Text>
          ) : null}
        </div>

        {itemChoice ? (
          <Text as="p" size="xs" tone="muted" className="mt-0.5">
            {itemChoice}
          </Text>
        ) : null}

        {freeItems ? (
          <div className="mt-1.5 flex">
            <CouponFreeItemAction terms={coupon.terms} tone="store" />
          </div>
        ) : null}

        {addItems || canReplace ? (
          <div className="-mb-1.5 -mr-2 flex justify-end">
            {addItems ? (
              <Button variant="ghost" tone="neutral" size="xs" weight="semibold" onClick={addItems}>
                <Text as="span" size="xs" tone="primary">
                  Adicionar itens
                </Text>
              </Button>
            ) : (
              <Button
                variant="ghost"
                tone="neutral"
                size="xs"
                weight="semibold"
                aria-label={`Trocar cupom ${coupon.code}`}
                onClick={() => setReplacing(true)}
              >
                <Text as="span" size="xs" tone="muted">
                  Trocar
                </Text>
              </Button>
            )}
          </div>
        ) : null}

        {replaceForm && replacing ? <div className="mt-2">{replaceForm(() => setReplacing(false))}</div> : null}
      </div>
    </CouponTicket>
  )
}
